import {
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  afterNextRender,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

import { CellStyle, Column, GridApi, IRowNode, RowNode } from 'ag-grid-community';

import { FontIconComponent } from '@camelot/icons';
import { EmptyComponent } from '@camelot/ui';
import { isNonNullable } from '@camelot/utils';

import { resolveTableCellClass, resolveTableCellStyle } from '../../helpers/table-params';
import { CamTranslationGrid } from '../../translation.service';
import { AbstractGridComponent } from '../abstract-grid.component';
import { LoadingComponent } from '../loading/loading.component';
import { GridTableCellComponent } from './cell/grid-table-cell.component';
import { GridTableHeaderCellComponent } from './header-cell/grid-table-header-cell.component';

export type GridTableRowSize = 'sm' | 'md' | 'lg' | 'grid';

/**
 * Vue « tableau custom » d'une grille (`GridViewType = 'table'`), rendue par `cam-ag-grid` à la place
 * de la grille AG Grid (qui reste montée, à 0px, comme en vue `card`).
 *
 * Aucun état propre : la table est une projection de l'état AG Grid de la grille, lue via son api.
 * - Colonnes : `getAllDisplayedColumns()` — donc les champs sélectionnés (`cam-fields-selection`,
 *   vues sauvegardées), dans l'ordre de la grille.
 * - Lignes : les nœuds affichés de la page courante (`getDisplayedRowAtIndex`) — filtres, tri,
 *   pagination et arbre sont ceux de la grille ; un nœud non chargé (server-side) affiche un loader.
 * - Cellules / en-têtes : `cam-grid-table-cell` / `cam-grid-table-header-cell` réutilisent les
 *   `cellRenderer` / `headerComponent` des `ColDef` (type de colonne + `fieldsOverrides`).
 *
 * Tous les événements AG Grid arrivent hors zone Angular : l'état est publié dans des signaux.
 */
@Component({
  host: { '[class]': "'row-' + rowSize()", '[style.--grid-table-row-height.px]': 'rowHeight()' },
  imports: [EmptyComponent, FontIconComponent, GridTableCellComponent, GridTableHeaderCellComponent, LoadingComponent],
  selector: 'cam-grid-table',
  styleUrl: './grid-table.component.scss',
  templateUrl: './grid-table.component.html',
})
export class GridTableComponent<T extends { id: string }>
  extends AbstractGridComponent<T>
  implements OnInit, OnDestroy
{
  autoPageSize = input(false);
  rowHeight = input(40);
  rowSize = input<GridTableRowSize>('md');

  public readonly columns = signal<Column[]>([]);
  public readonly rows = signal<IRowNode<T>[]>([]);
  /** Bumpé à chaque changement de modèle / colonnes / tri / filtre : les cellules se re-rendent. */
  public readonly version = signal(0);
  /** Bumpé à chaque changement de sélection : ne re-rend pas les cellules, seulement la classe de ligne. */
  public readonly selectionVersion = signal(0);

  protected override _createGrid = false;

  private readonly _api = signal<GridApi<T> | null>(null);
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _injector = inject(Injector);
  private readonly _resizeObserver = new ResizeObserver(() => this._applyAutoPageSize());

  private readonly _modelEvents = [
    'cellValueChanged',
    'displayedColumnsChanged',
    'filterChanged',
    'modelUpdated',
    'paginationChanged',
    'rowDataUpdated',
    'sortChanged',
  ];
  private readonly _selectionEvents = ['rowSelected', 'selectionChanged'];
  private readonly _onModelChanged = () => this.refresh();
  private readonly _onSelectionChanged = () => this.selectionVersion.update(version => version + 1);

  get api() {
    return this._api();
  }

  constructor() {
    super();
    CamTranslationGrid.getInstance();
    effect(() => {
      if (this.autoPageSize()) {
        this._applyAutoPageSize();
      }
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this._registerSubscription(this._grid.ready$.subscribe(ready => (ready ? this._attach() : this._detach())));
    this._resizeObserver.observe(this._host.nativeElement);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this._resizeObserver.disconnect();
    this._detach();
  }

  public refresh() {
    const api = this._api();
    if (!api) {
      this.columns.set([]);
      this.rows.set([]);
      return;
    }
    this.columns.set(api.getAllDisplayedColumns());
    this.rows.set(this._getPageRows(api));
    this.version.update(version => version + 1);
    if (this.autoPageSize()) {
      afterNextRender(() => this._applyAutoPageSize(), { injector: this._injector });
    }
  }

  public cellClass(column: Column, node: IRowNode<T>): string[] {
    const api = this._api();
    return api && node.data ? resolveTableCellClass(api, column, node) : [];
  }

  public cellStyle(column: Column, node: IRowNode<T>): CellStyle | null {
    const api = this._api();
    return api && node.data ? resolveTableCellStyle(api, column, node) : null;
  }

  public clickRow(node: IRowNode<T>, event: MouseEvent) {
    this._grid.clickRow(node as RowNode<T>, event);
  }

  public isSelected(node: IRowNode<T>): boolean {
    this.selectionVersion();
    return node.isSelected() ?? false;
  }

  public toggleExpanded(node: IRowNode<T>, event: Event) {
    event.stopPropagation();
    node.setExpanded(!node.expanded);
  }

  public trackColumn(column: Column): string {
    return column.getColId();
  }

  public trackRow(node: IRowNode<T>): string {
    return node.id ?? String(node.rowIndex);
  }

  private _attach() {
    // `CamGridData.api` est typé `ag-grid-enterprise`, les renderers / headers (`ag-grid-angular`)
    // attendent les types `ag-grid-community` : même API, déclarations distinctes (cf. `AgGridComponent.isReady`).
    const api = this._grid.api as unknown as GridApi<T> | null;
    if (!api || api === this._api()) {
      return;
    }
    this._detach();
    this._api.set(api);
    this._modelEvents.forEach(event => api.addEventListener(event, this._onModelChanged));
    this._selectionEvents.forEach(event => api.addEventListener(event, this._onSelectionChanged));
    this.refresh();
  }

  private _detach() {
    const api = this._api();
    if (!api) {
      return;
    }
    this._modelEvents.forEach(event => api.removeEventListener(event, this._onModelChanged));
    this._selectionEvents.forEach(event => api.removeEventListener(event, this._onSelectionChanged));
    this._api.set(null);
    this.refresh();
  }

  private _applyAutoPageSize() {
    const api = this._api();
    const host = this._host.nativeElement;
    if (!api || !this.autoPageSize() || host.clientHeight === 0) {
      return;
    }
    const container = host.querySelector<HTMLElement>('.grid-table-container');
    const containerBorders = container ? container.offsetHeight - container.clientHeight : 0;
    const headerHeight = host.querySelector<HTMLElement>('thead')?.offsetHeight ?? 0;
    const rowHeight =
      host.querySelector<HTMLElement>('.grid-table-row:not(.is-loading)')?.offsetHeight || this.rowHeight();
    const pageSize = Math.max(1, Math.floor((host.clientHeight - containerBorders - headerHeight) / rowHeight));

    if (pageSize !== api.paginationGetPageSize()) {
      api.setGridOption('paginationPageSize', pageSize);
    }
  }

  /** Nœuds affichés de la page courante — toutes les lignes quand la grille n'est pas paginée. */
  private _getPageRows(api: GridApi<T>): IRowNode<T>[] {
    const count = api.getDisplayedRowCount();
    const paginated = api.getGridOption('pagination') ?? false;
    const pageSize = api.paginationGetPageSize();
    const start = paginated ? api.paginationGetCurrentPage() * pageSize : 0;
    const end = paginated ? Math.min(start + pageSize, count) : count;

    return Array.from({ length: Math.max(end - start, 0) }, (_, index) =>
      api.getDisplayedRowAtIndex(start + index)
    ).filter(isNonNullable);
  }
}
